import { AddressData } from '@/common/types/address'
import { GeoData, GeoGeoableData } from '@/common/types/geoGeoable'
import { LogType } from '@/common/types/lead/log.type'
import { MetaResponse } from '@/common/types/metaResponse'
import { UserData } from '@/common/types/users'
import { stateStrategy } from '../constants/stateStrategy.constant'
import { EstablishmentCompanyData } from '@/common/types/establishment/company.type'
import { FeeTemplateData } from '@/features/feeTemplates/types/feeTemplates.type'
import { PrinterMachineData } from '@/common/types/printerMachine.type'
import { ProductCategoryData } from '@/common/types/productCategory.type';
import { ProductData } from '@/common/types/product.type';

export type RequirementListSummaryData = {
  description: string
  steps: {
    name: string
    passed: boolean
  }[]
}

export type BiddingStatus = 'undisputed' | 'victory' | 'loss'

export type BiddingInformationData = {
  contest_date?: string
  geo_geoables?: GeoGeoableData[]
  government_segment?: string
  last_review?: LastReview
  minimum_establishments_in_region?: number
  notice_number?: string
  object_of_the_bidding?: string
  estimated_equipment_total?: number
  status?: BiddingStatus
}

export type SpecialEntriesData = {
  id: string
  fee_template: FeeTemplateData
  temporary_target_id?: string
  temporary_target_name?: string
  temporary_target_type?: string
  target?: EstablishmentCompanyData
  value: number
}

export type FiscalDocumentInformationData = {
  consumption_observations?: string
  service_observations?: string
  seller_cnpj?: string
  fiscal_document_kind?: string
  last_review?: LastReview
}

export type GeneralInformationData = {
  cnpj?: string
  email?: string
  fantasy_name?: string
  official_name?: string
  primary_cnae?: string
  score?: string
  state_inscription?: string
  company_logo?: {
    url: string
    content_type: string
  }
  last_review?: LastReview
}

export type ContractEquipmentCategoryData = {
  id: string
  equipment_category: {
    id: string
    description: string
    display_name: string
  }
  quantity: number
}

export type ContractFeeData = {
  id: string
  fee_template: FeeTemplateData
  value: number
  main_fee: boolean
}

export type ContractProductCategoryData = {
  id: string
  contract_product_category_products: ContractProductCategoryProductData[]
  global_value_in_currency?: string
  global_value_in_unit_for_consumption?: string
  product_category: ProductCategoryData
}

export type ContractProductCategoryProductData = {
  id: string
  global_value_in_currency?: string
  global_value_in_unit_for_consumption?: string
  contract_product_category_product_prices: ContractProductCategoryProductPriceData[]
  product: ProductData
  price_rule?: string
}

export type ContractProductCategoryProductPriceData = {
  id: string
  geo: GeoData
  value: string
}

export type Cycle = 'weekly' | 'fortnightly' | 'monthly' | 'fixed'

export type ContractData = {
  id: string
  bill_grouping_mode?: string
  bill_by?: string
  cycle?: Cycle
  cycle_start_day?: number
  cycle_fixed_interval?: number
  closing_to_due_in_days?: number
  cnpj_for_fiscal_document?: string
  consumption_controlled_by: 'currency' | 'quantity'
  contract_equipment_categories?: ContractEquipmentCategoryData[]
  contract_fees?: ContractFeeData[]
  contract_product_categories?: ContractProductCategoryData[]
  granted_credit?: number
  global_value?: string
  due_to_suspension_in_days?: number
  duration_in_months?: number
  file: {
    content_type?: string
    url?: string
  }
  fiscal_document_consumer?: string
  fiscal_document_requirement?: string
  geo_geoables?: GeoGeoableData[]
  in_effect_since?: string
  in_effect_until?: string
  last_review?: LastReview
  payment_method?: string
  payment_modality?: string
  protocol_number?: string
  state: string
  require_product_credit_quota: boolean;
  default_fuel_price_rule: string;
}

export type LastReview = {
  description?: string
  state: 'disapproved' | 'approved'
}

export enum ContactState {
  pending = 'pending',
  awaiting_client_documents = 'awaiting_client_documents',
  in_review = 'in_review',
  complete = 'complete',
}

export type ContactData = {
  id: string
  email?: string
  full_name?: string
  last_review?: LastReview
  role_in_company?: string
  phone?: {
    number: string
    kind: string
    whatsapp_capability: string
  }
  responsible_for_company_information?: boolean
  cpf?: string
  documents: {
    id?: string
    cpf?: string
    file?: {
      id: string
      url: string
      content_type: string
    }
    issued_on_date?: string
    expires_at?: string
    rg?: string
    type?: string
    last_review?: LastReview
    uploaded_by?: UserData
    cnh?: string
  }[]
  requirement_list_summary?: RequirementListSummaryData
  state: ContactState
}

export type LastStateChangeLog = {
  changed_by: UserData
  changed_by_type: string
  created_at: string
  loggable_id: string
  loggable_type: string
  message: string
  next_state: keyof typeof stateStrategy
  previous_state: keyof typeof stateStrategy
  source_id: string
  source_type: string
}

export type PublicCompanyLeadData = {
  id: string
  display_name: string
  observations: string
  state: keyof typeof stateStrategy
  creator: UserData
  commercial_manager: UserData
  commercial_manager_substitute: UserData | null
  contact_for_master_user?: ContactData
  bidding_information?: BiddingInformationData
  fee_rule?: {
    special_entries: SpecialEntriesData[]
    last_review: LastReview
  }
  fiscal_document_information?: FiscalDocumentInformationData
  general_information?: GeneralInformationData
  generic_documents: {
    id: string
    attachment_id: string
    content_type: string
    url: string
  }[]
  address?: AddressData & { last_review: LastReview }
  contacts?: ContactData[]
  contract?: ContractData
  requirement_list_summary?: RequirementListSummaryData
  last_review: LastReview
  logs?: LogType[]
  created_at: string
  archived_at?: string
  last_state_change_log?: LastStateChangeLog
  printer_machine?: PrinterMachineData
}

export type PublicCompanyLeadsResponse = {
  data: {
    data: PublicCompanyLeadData[]
    meta: MetaResponse
  }
}

export type PublicCompanyLeadResponse = {
  data: {
    data: PublicCompanyLeadData
  }
}

export type PublicCompanyLeadContactResponse = {
  data: {
    data: ContactData
  }
}

export type PublicCompanyLeadTransformerResponse = {
  registered?: PublicCompanyLeadData[]
  qualifying?: PublicCompanyLeadData[]
  certame?: PublicCompanyLeadData[]
  negotiating?: PublicCompanyLeadData[]
  implantation?: PublicCompanyLeadData[]
}
